import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Volume2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';

const LetsExplore = () => {
  const navigate = useNavigate();
  const [predictionStatus, setPredictionStatus] = useState('loading');
  const [prediction, setPrediction] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [currentFact, setCurrentFact] = useState(0);
  const audioRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  const wisdomQuotes = [
    "Reading the cosmic patterns in your birth chart",
    "Analyzing planetary positions at your birth moment",
    "Calculating Dasha periods that shape your journey",
    "Interpreting house placements and significance",
    "Examining planetary aspects and influences",
    "Correlating your chart with ancient wisdom",
    "Consulting centuries-old astrological texts",
    "Decoding karmic patterns in your horoscope",
    "Synthesizing knowledge with your unique chart",
    "Weaving the threads of your cosmic destiny",
    "Crafting your entire life blueprint precisely",
    "Great insights take time to ensure accuracy",
    "Each detail reveals your life's true path",
    "Precision in astrology reveals deepest truths"
  ];
  
  useEffect(() => {
    const predictionId = localStorage.getItem('prediction_id');
    if (!predictionId) {
      toast.error('No prediction found');
      setPredictionStatus('error');
      navigate('/');
      return;
    }
    
    checkPredictionStatus();
    pollIntervalRef.current = setInterval(checkPredictionStatus, 3000);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);
  
  useEffect(() => {
    if (predictionStatus === 'loading') {
      const factInterval = setInterval(() => {
        setCurrentFact(prev => (prev + 1) % wisdomQuotes.length);
      }, 4000);
      return () => clearInterval(factInterval);
    }
  }, [predictionStatus]);
  
  const checkPredictionStatus = async () => {
    const predictionId = localStorage.getItem('prediction_id');
    if (!predictionId) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/story/status/${predictionId}`);
      
      if (!response.ok) throw new Error('Failed');
      
      const data = await response.json();
      
      if (data.status === 'completed') {
        setPredictionStatus('ready');
        setPrediction(data.past_prediction || '');
        setAudioUrl(data.audio_url || '');
        
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        toast.success('Your story is ready');
      } else if (data.status === 'error') {
        setPredictionStatus('error');
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        toast.error('Failed to generate story');
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };

  const handlePlayAudio = () => {
    if (!audioUrl) {
      toast.error('Audio not available');
      return;
    }
    
    setPredictionStatus('playing');
    
    if (audioRef.current) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      audioRef.current.src = `${backendUrl}${audioUrl}`;
      audioRef.current.play();
      
      audioRef.current.onended = () => {
        setPredictionStatus('completed');
        toast.success('Past story complete');
      };
      
      audioRef.current.onerror = () => {
        toast.error('Failed to play audio');
        setPredictionStatus('ready');
      };
    }
  };

  return (
    <CosmicBackground showAstroChart={false}>
      <div className="min-h-screen relative px-4 py-8 md:px-8 lg:px-16">
        <div className="flex flex-col items-center justify-center min-h-screen max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-serif text-white mb-4 text-center tracking-wide">
            Sage Vaarta
          </h1>
          <p className="text-gray-400 text-center mb-12 text-sm md:text-base">
            Your Past Health Journey
          </p>

          <div className="relative w-full aspect-video bg-gradient-to-br from-gray-900/80 to-black/80 rounded-3xl border border-white/10 overflow-hidden mb-8 shadow-2xl backdrop-blur-xl">
            
            {predictionStatus === 'loading' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
                <div className="relative w-48 h-48 md:w-64 md:h-64 mb-12">
                  <div className="absolute inset-0 border-2 border-[#D4AF37] rounded-full opacity-20" style={{animation: 'spin 8s linear infinite'}} />
                  <div className="absolute inset-8 border border-[#D4AF37] rounded-full opacity-40" style={{animation: 'spin 6s linear infinite reverse'}} />
                  <img 
                    src="/sage-final.png" 
                    alt="Sage" 
                    className="absolute inset-0 w-full h-full object-contain p-8"
                    style={{
                      filter: 'brightness(1.1) contrast(1.05) saturate(0.95)',
                      animation: 'pulse 3s ease-in-out infinite'
                    }}
                  />
                </div>
                
                <div className="text-center space-y-6 max-w-2xl">
                  <p 
                    className="text-white text-xl md:text-2xl font-serif tracking-wide leading-relaxed"
                    style={{
                      fontFamily: 'Georgia, serif',
                      animation: 'fadeIn 2s ease-in-out infinite'
                    }}
                  >
                    {wisdomQuotes[currentFact]}
                  </p>
                  
                  <p className="text-gray-400 text-sm md:text-base font-light">
                    Your life blueprint is being crafted with precision
                  </p>
                  
                  <div className="flex justify-center gap-2 pt-6">
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '200ms'}} />
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '400ms'}} />
                  </div>
                </div>
              </div>
            )}
            
            {predictionStatus === 'ready' && (
              <>
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900/30 to-pink-900/30">
                  <div className="text-center">
                    <div className="relative w-48 h-48 md:w-64 md:h-64 mx-auto mb-6">
                      <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37]" />
                      <img 
                        src="/sage-final.png" 
                        alt="Sage" 
                        className="w-full h-full object-contain p-6"
                        style={{filter: 'brightness(1.2)'}}
                      />
                    </div>
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-white text-xl md:text-2xl font-serif mb-2">Your Story is Ready</p>
                    <p className="text-gray-300 text-sm md:text-base">
                      Click play to hear the narration
                    </p>
                  </div>
                </div>
                <button
                  onClick={handlePlayAudio}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors group"
                >
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-10 h-10 md:w-12 md:h-12 text-black ml-1" />
                  </div>
                </button>
              </>
            )}
            
            {predictionStatus === 'playing' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
                <div className="text-center">
                  <div className="relative w-56 h-56 md:w-72 md:h-72 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37] animate-pulse" />
                    <img 
                      src="/sage-final.png" 
                      alt="Sage" 
                      className="w-full h-full object-contain p-6 animate-pulse"
                      style={{filter: 'brightness(1.3)'}}
                    />
                  </div>
                  <Volume2 className="w-10 h-10 text-yellow-400 mx-auto mb-3 animate-pulse" />
                  <p className="text-white text-xl md:text-2xl font-serif mb-4">The Sage Speaks</p>
                  <div className="flex justify-center gap-2">
                    {[0, 150, 300, 450, 600].map((delay, i) => (
                      <div key={i} className="w-2 h-12 md:w-3 md:h-16 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: `${delay}ms`}} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {predictionStatus === 'completed' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-green-900/50 to-blue-900/50">
                <div className="text-center px-8">
                  <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-4" />
                  <p className="text-white text-2xl font-serif mb-3">Story Complete</p>
                  <p className="text-gray-300 text-sm mb-6">
                    Visit dashboard to unlock future predictions
                  </p>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black rounded-full font-semibold text-lg hover:scale-105 transition-transform"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            )}
            
            {predictionStatus === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-8">
                  <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <p className="text-red-400 text-lg mb-4">Failed to load story</p>
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-[#D4AF37] text-black rounded-full font-semibold"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            )}
          </div>

          {prediction && (predictionStatus === 'ready' || predictionStatus === 'playing') && (
            <>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 max-w-3xl w-full border border-white/10 mb-6">
                <h3 className="text-[#D4AF37] text-lg font-serif mb-3">Your Story</h3>
                <div className="text-gray-300 text-sm leading-relaxed max-h-64 overflow-y-auto">
                  {prediction.split('\n').map((para, idx) => (
                    <p key={idx} className="mb-2">{para}</p>
                  ))}
                </div>
              </div>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-full font-medium hover:bg-white/20 transition-all"
              >
                Skip to Dashboard
              </button>
            </>
          )}
          
          <audio ref={audioRef} className="hidden" />
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </CosmicBackground>
  );
};

export default LetsExplore;
